import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { UserService } from './user.service';
import { ICustomer } from '../models/ICustomer';
import { HttpClient, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import {of as observableOf, Observable, BehaviorSubject} from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map } from 'rxjs/operators';

@Injectable()
export class CustomerService {

  private selectedInstitutionMetaBS: BehaviorSubject<ICustomer|null> = new BehaviorSubject<ICustomer>(null);
  selectedInstitutionMeta$ = this.selectedInstitutionMetaBS.asObservable();

  constructor(
    private http: HttpClient,
    private userService: UserService,
  ) { }

  public static normalizeCustomers(customers): ICustomer[] {
    return customers.map(
      customer => {
        customer.id = parseInt(customer.id, 10);
        customer.customerActiveFlag = customer.customerActiveFlag === 'true';
        return customer;
      }
    );
  }

  set selectedInstitutionMeta(institution: ICustomer) {
    this.selectedInstitutionMetaBS.next(institution);
  }

  get institution(): ICustomer {
    const inst = JSON.parse(localStorage.getItem('institution'));
    if (inst) {
      return inst;
    }
  }

  set institution(customer: ICustomer) {
    localStorage.setItem('institution', JSON.stringify(customer));
  }

  get childInstitutions(): ICustomer[] {
    const childrenObj = localStorage.getItem('childInstitutions');
    if (childrenObj) {
      const children = JSON.parse(childrenObj);
      if (children) {
        return children;
      }
    }
    return null;
  }

  set childInstitutions(customers: ICustomer[]) {
    localStorage.setItem('childInstitutions', JSON.stringify(customers));
  }

  get hasAccessToSingleInstitution() {
    const hasAccessToSingleInstitution = JSON.parse(localStorage.getItem('hasAccessToSingleInstitution'));
    if (hasAccessToSingleInstitution) {
      return hasAccessToSingleInstitution;
    }
  }

  set hasAccessToSingleInstitution(access: boolean) {
    localStorage.setItem('hasAccessToSingleInstitution', JSON.stringify(access));
  }

  search(terms: Observable<string>, numberOfResults: number, isUserSuperAdmin: boolean): Observable<ICustomer[]> {
   return terms.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(term => {
        if (term) {
          return isUserSuperAdmin ?
            this.superAdminSearch(term, numberOfResults) :
            this.adminSearch(term, numberOfResults);
        } else {
          return observableOf([]);
        }
      }));
  }

  getCustomersWithChildStatus(): Observable<ICustomer[]> {
    const ids = this.userService.getUserAssociationIds();
    const params = new HttpParams({encoder: new HttpUrlEncodingCodec()}).set('customerStatus', 'Active');
    return this.http.post<ICustomer[]>(`${environment.customerApiUrl}/noauth/customersWithChildStatus`, ids, {params});
  }

  getChildrenWithChildStatus(id: number): Observable<ICustomer[]> {
    const params = new HttpParams({encoder: new HttpUrlEncodingCodec()}).set('customerStatus', 'Active');
    return this.http.get<ICustomer[]>(`${environment.customerApiUrl}/noauth/id/${id}/childrenWithChildStatus`, {params});
  }

  getInstitutionMetaData(id: number = this.institution.id): Observable<ICustomer> {
    return this.http.get<ICustomer>(`${environment.customerApiUrl}/customers/id/${id}`);
  }

  getOrderedAncestorIds() {
    const params = new HttpParams({encoder: new HttpUrlEncodingCodec()}).set('customerStatus', 'Active');
    return this.http.get(`${environment.customerApiUrl}/customers/${this.institution.id}/orderedAncestors`, {params});
  }

  private superAdminSearch(likeName: string, size: number, from: number = 0): Observable<ICustomer[]> {
    const requestOptions = this.createRequestOptions({
      likeName,
      predefinedOrder: 'true',
      customerStatus: 'All',
      from: from.toString(),
      size: size.toString(),
      searchByCustomerNumber: 'true'
    });
    return this.http.get<ICustomer[]>
      (`${environment.customerApiUrl}/search/superUserCustomers`, requestOptions).pipe(map(CustomerService.normalizeCustomers));
  }

  private adminSearch(likeName: string, size: number, from: number = 0): Observable<ICustomer[]> {
    const body = {
      likeName,
      customerIds: this.userService.getUserAssociationIds(),
      predefinedOrder: 'true',
      customerStatus: 'All',
      from: from.toString(),
      size: size.toString(),
      searchByCustomerNumber: 'true'
    };
    return this.http.post<ICustomer[]>(`${environment.customerApiUrl}/search/children`, body).pipe(map(CustomerService.normalizeCustomers));
  }

  private createRequestOptions(paramsObj?: {[key: string]: string}) {
    const params: HttpParams = paramsObj
      ? Object.keys(paramsObj).reduce(
        (previous, key) => previous.set(key, paramsObj[key]),
        new HttpParams({ encoder: new HttpUrlEncodingCodec()}))
      : undefined;
    return { params };
  }
}
